import { LoaderFunction } from 'react-router-dom';
import {
  productionStatisticService,
  GetProductionStatisticDataRes,
} from '../services/productionStatistic.service.ts';
import { zipWith, take, map } from 'rxjs';
import { responseHandler } from '../../../shared/responseHandler/responseHandler.ts';
import { getFirstAndLastDateOfCurrentMonth } from '../../../shared/helpers/utils/utils.helper.ts';
import { storageHelper } from '../../../shared/helpers/storage';
import { ISelectOption } from '../../../shared/components/form/fields/select.component.tsx';
import {
  IItem as IFilterDictItem,
  IItem,
} from '../../../shared/components/filterTableWithModal/filterTableWithModal.component.tsx';
import {
  createOrderService,
  DictOneTimeCustomersQueryRes,
} from '../../order/services/createOrder.service.ts';
import { customerService, ListCustomersRes } from '../../customer/services/customer.service.ts';
import { articleService, ArticlesListRes } from '../../article/services/article.service.ts';
import { C_Group_By_Type } from '../../../graphql/generatedModel.ts';
import {
  configsData,
  DictSubCompaniesRes,
} from '../../../shared/services/configsData/configsData.service.ts';
import { getI18n } from 'react-i18next';

export const initProductionStatisticLoader: IProductionStatisticLoader = {
  gridData: [],
  summaryData: {},
  chartsDataset: {
    keys: [],
    values: [],
  },
  datePeriod: {
    ...getFirstAndLastDateOfCurrentMonth(),
  },
  profileId: undefined,
  profileOptions: [],
  filter: {
    customerList: [],
    articleList: [],
    subCompaniesList: [],
  },
  gridGrouping: [],
  filterDictionaries: {
    oneTimeCustomers: [],
    customers: [],
    articles: [],
    subCompanies: [],
  },
  groupBy: C_Group_By_Type.GBT1_ARTICLE,
};

export const productionStatisticLoader: LoaderFunction =
  async (): Promise<IProductionStatisticLoader> => {
    const initValues = structuredClone(initProductionStatisticLoader);
    const sessionDatePeriod = storageHelper.session.getItem('productionStatistic.datePeriod');
    initValues.profileOptions = storageHelper.local.getItem('productionStatistic.profiles') || [];
    initValues.profileId =
      storageHelper.local.getItem('productionStatistic.selectedProfileId') || undefined;
    if (sessionDatePeriod) {
      initValues.datePeriod = sessionDatePeriod;
    }
    if (initValues.profileId) {
      const sessionProfile = initValues.profileOptions.find((el) => el.id === initValues.profileId);
      if (sessionProfile) {
        initValues.filter = sessionProfile.settings.filter;
        initValues.gridGrouping = sessionProfile.settings.gridGrouping || [];
        initValues.groupBy = sessionProfile.settings.groupBy || C_Group_By_Type.GBT1_ARTICLE;
      }
    }
    const filterDictionaries = await productionStatisticService.globHelpers.streamToPromise(
      createOrderService.sub.oneTimeCustomerList().pipe(
        zipWith(
          customerService.sub.listGridCustomers(),
          articleService.sub.articleList(),
          configsData.sub.dictSubCompanies(),
        ),
        responseHandler<
          [DictOneTimeCustomersQueryRes, ListCustomersRes, ArticlesListRes, DictSubCompaniesRes]
        >({
          errorReturnType: [[], [], [], []],
        }),
        take(1),
        map(([oneTimeCustomersRaw, customersRaw, articlesRaw, subCompaniesRaw]) => {
          const oneTimeCustomers = oneTimeCustomersRaw.map(({ id, label, customerNo }) => ({
            id,
            number: customerNo,
            description: label ?? '',
          }));

          const customers = customersRaw.map(({ id, customerNo, internalOrFullName }) => ({
            id,
            number: customerNo,
            description: internalOrFullName ?? '',
          }));

          const articles = articlesRaw.map(({ id, articleNo, description }) => ({
            id,
            number: articleNo,
            description: description ?? '',
          }));

          const subCompanies = subCompaniesRaw.map(({ id, label }) => ({
            id,
            number: '',
            description: label ?? '',
          }));

          // Add record with id = 'null' for filtering items without assigned subCompany
          subCompanies.unshift({
            id: 'null',
            number: '',
            description: getI18n().t('common.default'),
          });
          return {
            filterDictionaries: {
              oneTimeCustomers,
              customers,
              articles,
              subCompanies,
            },
          };
        }),
      ),
      () => {
        createOrderService.pub.oneTimeCustomerList();
        customerService.pub.listGridCustomers({ filter: { isActive: true }, cache: false });
        articleService.pub.articlesList({ filter: { isActive: true }, cache: false });
        configsData.pub.common(['dictSubCompanies']);
      },
    );
    return { ...initValues, ...filterDictionaries };
  };

export interface IProductionStatisticLoader {
  gridData: GetProductionStatisticDataRes['gridData'];
  chartsDataset: GetProductionStatisticDataRes['chartsDataset'];
  summaryData: GetProductionStatisticDataRes['summaryData'];
  datePeriod: {
    fromDate: string;
    toDate: string;
  };
  profileOptions: Array<IProdExtendedOptions>;
  profileId: string | undefined;
  filter: {
    customerList: IItem[];
    articleList: IItem[];
    subCompaniesList: IItem[];
  };
  gridGrouping?: Array<string>;
  filterDictionaries: {
    oneTimeCustomers: IFilterDictItem[];
    customers: IFilterDictItem[];
    articles: IFilterDictItem[];
    subCompanies: IFilterDictItem[];
  };
  groupBy: C_Group_By_Type;
}

export interface IProdExtendedOptions extends ISelectOption {
  settings: Pick<IProductionStatisticLoader, 'filter' | 'gridGrouping' | 'groupBy'>;
}
